import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { isPlaceholderEntityName } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import useResolveManualRide from "../hooks/useResolveManualRide";
import useSetManualRideRing from "../hooks/useSetManualRideRing";
import useSetManualRideDetails from "../hooks/useSetManualRideDetails";
import { classNumberLabel, sortByClassNumber } from "../util/classDisplay";

// #387 — per-row "attach to real ride" picker for the emergency manual-ride
// review queue. Suggests the target ride by matching CNO (manual ride's
// competitorNumber == FVD ride's competitorNumber, #386's keying scheme),
// and shows the manual ride's assigned test next to the candidate's test so
// the admin can see a mismatch before trying to attach. A mismatch
// HARD-BLOCKS the Attach button client-side — the server also 409s (code
// "test_mismatch", #388) as the authoritative enforcement, shown here as an
// inline fallback in case of a race or a bypassed client check.
//
// #3354 — a mismatch is no longer a dead end: the admin can tick an explicit
// "attach anyway" confirm (sent as confirmTestMismatch, audited server-side
// with the tests crossed). The same dialog also lets the admin set the manual
// ride's rider and test (PUT /ride/:rideId/details), so the rider portal can
// find the ride and the assigned test can match the real one. A server-side
// 409 test_mismatch (a manual ride with no assigned test whose sheets still
// differ from the target) reveals the same confirm box.
//
// #2102 — candidates are scoped to the manual ride's whole SHOW
// (GET /ride?showId=), not its own class. A wrong-class manual ride (the
// scribe picked the wrong class off the day sheet) needs to find its real
// ride in a DIFFERENT class than the one it was created against; the
// server-side resolveManualRide has never enforced same-class (only
// same-org + same-testId), so this was purely a client-side hole. Each
// candidate's class is resolved and shown (classesById below) so same-test
// rides in different classes are still distinguishable. Per queue planning
// (#2911, dropping the ridesByTest GSI): this deliberately uses the
// showId-scoped GET /ride?showId=, never ridesByTest.
//
// #3238 — the whole-show list was one long mixed list. A Class dropdown now
// scopes the ride picker: it defaults to the manual ride's own class, shows
// that class's rides in running order (scheduledTime), and the #2918 CNO
// suggestion runs inside the selected class. Changing the class re-runs the
// suggestion, so the #2102 wrong-class case is still one dropdown change away.
// The whole show is still fetched once (the server paginates it to
// completion, fetchRidesForShow) and filtered client-side, so no ride is
// silently dropped and a class change costs no round trip.
const riderLabelOf = (p) => [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || p.id;

export default function AttachRideDialog({ open, manualRide, onClose, onResolved }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const { resolve, resolving } = useResolveManualRide();
  const { setRing, saving: savingRing } = useSetManualRideRing();
  const { setDetails, saving: savingDetails } = useSetManualRideDetails();

  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [testsById, setTestsById] = useState({});
  const [classesById, setClassesById] = useState({});
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const [attachError, setAttachError] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState("");
  // #3355 -- the manual ride's ring (admin fallback edit): ringValue is the picker, ringSavedValue the last saved value ("" = none).
  const [ringValue, setRingValue] = useState("");
  const [ringSavedValue, setRingSavedValue] = useState("");
  const [ringMessage, setRingMessage] = useState(null);
  // #3354 -- rider/test of the manual ride: *Value is the picker, *SavedValue
  // the last saved one (the opening `manualRide` prop is a stale snapshot once
  // a save lands, so the mismatch check reads the saved values).
  const [riders, setRiders] = useState([]);
  // #3363 -- the org's other RIDERs (walk-ins with no ride in the show), loaded
  // once, the first time the Rider picker opens (GET /people?personType=RIDER,
  // one peopleByOrg GSI Query), so the dialog does not pay for it unless the
  // admin goes looking for a rider the show list lacks.
  const [orgRiders, setOrgRiders] = useState([]);
  const [orgRidersLoading, setOrgRidersLoading] = useState(false);
  const orgRidersRequested = useRef(false);
  const [riderValue, setRiderValue] = useState(null);
  const [riderSavedId, setRiderSavedId] = useState("");
  const [testValue, setTestValue] = useState("");
  const [testSavedId, setTestSavedId] = useState("");
  const [detailsMessage, setDetailsMessage] = useState(null);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  // #3535 -- the other way across a mismatch: attach AND switch the real ride onto
  // the manual ride's test. Mutually exclusive with confirmMismatch.
  const [changeTargetTest, setChangeTargetTest] = useState(false);
  const [serverMismatch, setServerMismatch] = useState(false);

  const classId = manualRide?.classId;
  // #2127 — a manual ride's own showId can be absent (createManualRide
  // creates showId-less emergency rides); fall back to the manual ride's own
  // class's showId when that happens, same resolution useReviewQueue already
  // relies on for showId-less rows.
  const manualShowId = manualRide?.showId;

  const resolveShowId = useCallback(async () => {
    if (manualShowId) return manualShowId;
    if (!classId) return null;
    const cls = await scoreApi.classes.getClass(classId).then(unwrap).catch(() => null);
    return cls?.showId ?? null;
  }, [manualShowId, classId, scoreApi]);

  const loadCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    setAttachError(null);
    try {
      const showId = await resolveShowId();
      if (!showId) {
        setCandidates([]);
        setTestsById({});
        setClassesById({});
        setRiders([]);
        return;
      }
      const [rides, classes, tests, showRiders] = await Promise.all([
        scoreApi.rides.listRides(undefined, showId).then(unwrap),
        scoreApi.classes.listClassesForShows({ showIds: [showId] }).then(unwrap).catch(() => []),
        scoreApi.tests.listTests(showId).then(unwrap).catch(() => []),
        scoreApi.people.listShowRiders(showId).then(unwrap).catch(() => []),
      ]);
      setRiders(showRiders || []);
      const real = (rides || []).filter(
        (r) => !r.isManual && (r.id ?? r.rideId) !== (manualRide?.id ?? manualRide?.rideId),
      );
      setCandidates(real);
      setClassesById(Object.fromEntries((classes || []).map((c) => [c.id, c])));
      setTestsById(Object.fromEntries((tests || []).map((tst) => [tst.id, tst])));
    } catch (err) {
      setCandidates([]);
      setAttachError({ message: err.serverErrorDetail || err.message || t("reviewQueue.attach.loadError") });
    } finally {
      setCandidatesLoading(false);
    }
  }, [resolveShowId, manualRide, scoreApi, t]);

  useEffect(() => {
    if (!open) return;
    setSelectedTargetId("");
    setSelectedClassId(classId ?? "");
    setAttachError(null);
    setRingValue(manualRide?.ring ?? "");
    setRingSavedValue(manualRide?.ring ?? "");
    setRingMessage(null);
    setRiderValue(null);
    setRiderSavedId(manualRide?.riderId ?? "");
    setOrgRiders([]);
    setOrgRidersLoading(false);
    orgRidersRequested.current = false;
    setTestValue(manualRide?.testId ?? "");
    setTestSavedId(manualRide?.testId ?? "");
    setDetailsMessage(null);
    setConfirmMismatch(false);
    setChangeTargetTest(false);
    setServerMismatch(false);
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId, manualShowId]);

  // #2918 — a CNO is an ENTRY number: it repeats across every class that
  // entry rides at the show, so since #2102 widened candidates to the whole
  // show, "first CNO match" is GSI-return-order luck. A same-CNO candidate
  // with a different test gets caught by the mismatch block below, but a
  // same-CNO, SAME-test ride in a different class (one entry in two divisions
  // of the same test) sails through and one click moves judge-signed sheets
  // onto the wrong ride. Stated tie-break order for multiple CNO matches:
  //   1. same testId as the manual ride (a different test is blocked anyway,
  //      so never suggest one over a valid target);
  //   2. nearest scheduledTime to the manual ride's (the Bello Acres real
  //      ride was 16s away — #2102's notes); a missing/unparseable time on
  //      either side ranks last;
  //   3. ride id ascending — a deterministic last resort so the pick never
  //      depends on the ridesByShow GSI's return order.
  // #3238 — class options: every class with a candidate ride, plus the
  // selected class itself (the manual ride's own class stays selectable even
  // when it has no real rides yet, so the empty state is explained in place
  // and the admin can still switch away).
  const classOptions = useMemo(() => {
    const ids = new Set(candidates.map((c) => c.classId).filter(Boolean));
    if (selectedClassId) ids.add(selectedClassId);
    const rows = [...ids].map((id) => classesById[id] || { id, className: id });
    return sortByClassNumber(rows);
  }, [candidates, classesById, selectedClassId]);

  // No class on the manual ride: fall back to the first option so the list is
  // never an unscoped dump.
  const activeClassId = selectedClassId || classOptions[0]?.id || "";

  // Rides of the active class, running order: scheduledTime ascending, a
  // missing/unparseable time last, ride id as the deterministic tie-break.
  const classCandidates = useMemo(() => {
    const timeOf = (c) => {
      const v = c.scheduledTime ? Date.parse(c.scheduledTime) : NaN;
      return Number.isNaN(v) ? Infinity : v;
    };
    return candidates
      .filter((c) => c.classId === activeClassId)
      .sort((a, b) => {
        const ta = timeOf(a);
        const tb = timeOf(b);
        if (ta !== tb) return ta < tb ? -1 : 1;
        return String(a.id ?? a.rideId ?? "").localeCompare(String(b.id ?? b.rideId ?? ""));
      });
  }, [candidates, activeClassId]);

  const handleClassChange = (nextClassId) => {
    setSelectedClassId(nextClassId);
    setConfirmMismatch(false);
    setChangeTargetTest(false);
    setServerMismatch(false);
    // Drop the previous class's pick so the CNO default below re-runs for the
    // new class.
    setSelectedTargetId("");
  };

  const suggested = useMemo(() => {
    if (manualRide?.competitorNumber == null) return null;
    const matches = classCandidates.filter(
      (c) => c.competitorNumber != null && String(c.competitorNumber) === String(manualRide.competitorNumber),
    );
    if (matches.length === 0) return null;
    const parseTime = (v) => (v ? Date.parse(v) : NaN);
    const manualTime = parseTime(manualRide.scheduledTime);
    const rankOf = (c) => {
      const testRank = testSavedId && c.testId === testSavedId ? 0 : 1;
      const cTime = parseTime(c.scheduledTime);
      const timeRank = Number.isNaN(cTime) || Number.isNaN(manualTime) ? Infinity : Math.abs(cTime - manualTime);
      return [testRank, timeRank, String(c.id ?? c.rideId ?? "")];
    };
    const ranked = [...matches].sort((a, b) => {
      const ra = rankOf(a);
      const rb = rankOf(b);
      for (let i = 0; i < ra.length; i += 1) {
        if (ra[i] < rb[i]) return -1;
        if (ra[i] > rb[i]) return 1;
      }
      return 0;
    });
    return ranked[0];
  }, [classCandidates, manualRide, testSavedId]);

  useEffect(() => {
    if (suggested && !selectedTargetId) {
      setSelectedTargetId(suggested.id ?? suggested.rideId ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);

  const selectedTarget = useMemo(
    () => classCandidates.find((c) => (c.id ?? c.rideId) === selectedTargetId) || null,
    [classCandidates, selectedTargetId],
  );

  const manualTest = testSavedId ? testsById[testSavedId] : null;
  const targetTest = selectedTarget?.testId ? testsById[selectedTarget.testId] : null;

  const mismatch =
    serverMismatch ||
    !!(selectedTarget && testSavedId && selectedTarget.testId && selectedTarget.testId !== testSavedId);

  // Whole show has no real ride (nothing synced yet) vs. just this class.
  const noCandidates = !candidatesLoading && candidates.length === 0;
  const noClassCandidates = !candidatesLoading && !noCandidates && classCandidates.length === 0;

  // #3355 -- ring choices are the rings the show's classes actually carry:
  // ring filters are exact matches, so a free-text typo would just recreate
  // the invisible-ride problem. The ride's current ring stays selectable.
  const ringOptions = useMemo(() => {
    const rings = new Set(Object.values(classesById).map((c) => c.ring).filter(Boolean));
    if (ringSavedValue) rings.add(ringSavedValue);
    return [...rings].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [classesById, ringSavedValue]);

  const handleSetRing = async () => {
    if (!manualRide || !ringValue) return;
    setRingMessage(null);
    try {
      await setRing(manualRide.id ?? manualRide.rideId, ringValue);
      setRingSavedValue(ringValue);
      setRingMessage({ severity: "success", text: t("reviewQueue.attach.ringSaved") });
      onResolved?.();
    } catch (err) {
      setRingMessage({
        severity: "error",
        text: err?.serverErrorDetail || err?.message || t("reviewQueue.attach.ringError"),
      });
    }
  };

  // The saved rider, resolved to a picker option (the show's riders, or the id
  // alone when the list hasn't loaded / the rider isn't in it).
  const riderOptions = useMemo(() => {
    const inShow = new Set(riders.map((p) => p.id));
    const rest = orgRiders
      .filter(
        (p) =>
          !inShow.has(p.id) &&
          !isPlaceholderEntityName(p.firstName) &&
          !isPlaceholderEntityName(p.lastName),
      )
      .sort((a, b) => riderLabelOf(a).localeCompare(riderLabelOf(b)));
    return [...riders, ...rest];
  }, [riders, orgRiders]);
  const loadOrgRiders = () => {
    if (orgRidersRequested.current) return;
    orgRidersRequested.current = true;
    setOrgRidersLoading(true);
    scoreApi.people
      .listPeople("RIDER")
      .then(unwrap)
      .then((list) => setOrgRiders(Array.isArray(list) ? list.filter((p) => p.personType === "RIDER") : []))
      // A failed lookup leaves the show's riders usable; allow a retry on the next open.
      .catch(() => {
        orgRidersRequested.current = false;
      })
      .finally(() => setOrgRidersLoading(false));
  };
  const savedRider = riderOptions.find((p) => p.id === riderSavedId) ?? null;
  const effectiveRider = riderValue ?? savedRider;
  const testOptions = useMemo(
    () =>
      Object.values(testsById)
        .map((tst) => ({ id: tst.id, label: tst.name || tst.testCode || tst.id }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [testsById],
  );
  const detailsChanged =
    (!!riderValue && riderValue.id !== riderSavedId) || (!!testValue && testValue !== testSavedId);

  const handleSaveDetails = async () => {
    if (!manualRide || !detailsChanged) return;
    setDetailsMessage(null);
    const body = {};
    if (riderValue && riderValue.id !== riderSavedId) body.riderId = riderValue.id;
    if (testValue && testValue !== testSavedId) body.testId = testValue;
    try {
      await setDetails(manualRide.id ?? manualRide.rideId, body);
      if (body.riderId) setRiderSavedId(body.riderId);
      if (body.testId) setTestSavedId(body.testId);
      // The assigned test changed under the server-side mismatch fallback.
      setServerMismatch(false);
      setConfirmMismatch(false);
      setChangeTargetTest(false);
      setDetailsMessage({ severity: "success", text: t("reviewQueue.attach.detailsSaved") });
      onResolved?.();
    } catch (err) {
      setDetailsMessage({
        severity: "error",
        text: err?.serverErrorDetail || err?.message || t("reviewQueue.attach.detailsError"),
      });
    }
  };

  const handleAttach = async () => {
    if (!manualRide || !selectedTargetId || (mismatch && !confirmMismatch && !changeTargetTest)) return;
    setAttachError(null);
    try {
      await resolve(manualRide.id ?? manualRide.rideId, selectedTargetId, {
        confirmTestMismatch: mismatch && confirmMismatch && !changeTargetTest,
        changeTargetTest: mismatch && changeTargetTest,
      });
      onResolved?.();
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "test_mismatch") {
        // #3354 -- not a dead end: surface the confirm box and let the admin retry.
        setServerMismatch(true);
        setAttachError({ message: t("reviewQueue.attach.serverMismatch") });
      } else {
        setAttachError({ message: err?.serverErrorDetail || err?.message || t("reviewQueue.attach.serverError") });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("reviewQueue.attach.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.attach.manualRideLabel")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" label={`CNO ${manualRide?.competitorNumber ?? "-"}`} />
              <Typography>{manualRide?.horseName || manualRide?.riderName || "-"}</Typography>
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="manual-ride-ring-label">{t("reviewQueue.attach.ringLabel")}</InputLabel>
                <Select
                  labelId="manual-ride-ring-label"
                  label={t("reviewQueue.attach.ringLabel")}
                  value={ringValue}
                  onChange={(e) => setRingValue(e.target.value)}
                >
                  {ringOptions.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={!ringValue || ringValue === ringSavedValue || savingRing}
                onClick={handleSetRing}
              >
                {savingRing ? <CircularProgress size={18} /> : t("reviewQueue.attach.setRing")}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {ringSavedValue ? t("reviewQueue.attach.ringHint") : t("reviewQueue.attach.ringMissing")}
            </Typography>
            {ringMessage && (
              <Alert severity={ringMessage.severity} sx={{ mt: 1 }}>
                {ringMessage.text}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("reviewQueue.attach.detailsHint")}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Autocomplete
                size="small"
                options={riderOptions}
                loading={orgRidersLoading}
                onOpen={loadOrgRiders}
                value={effectiveRider}
                getOptionLabel={riderLabelOf}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_e, next) => setRiderValue(next)}
                renderInput={(params) => <TextField {...params} label={t("reviewQueue.attach.riderLabel")} />}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="manual-ride-test-label">{t("reviewQueue.attach.testLabel")}</InputLabel>
                <Select
                  labelId="manual-ride-test-label"
                  label={t("reviewQueue.attach.testLabel")}
                  value={testOptions.some((o) => o.id === testValue) ? testValue : ""}
                  onChange={(e) => setTestValue(e.target.value)}
                >
                  {testOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Button variant="outlined" size="small" disabled={!detailsChanged || savingDetails} onClick={handleSaveDetails}>
                  {savingDetails ? <CircularProgress size={18} /> : t("reviewQueue.attach.saveDetails")}
                </Button>
              </Box>
            </Stack>
            {!riderSavedId && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t("reviewQueue.attach.noRiderSet")}
              </Typography>
            )}
            {detailsMessage && (
              <Alert severity={detailsMessage.severity} sx={{ mt: 1 }}>
                {detailsMessage.text}
              </Alert>
            )}
          </Box>

          {attachError && <Alert severity="error">{attachError.message}</Alert>}

          {candidatesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : noCandidates ? (
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={loadCandidates}>
                  {t("reviewQueue.attach.refresh")}
                </Button>
              }
            >
              {t("reviewQueue.attach.notSynced")}
            </Alert>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id="attach-class-label">{t("reviewQueue.attach.classLabel")}</InputLabel>
                <Select
                  labelId="attach-class-label"
                  label={t("reviewQueue.attach.classLabel")}
                  value={activeClassId}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  {classOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {classNumberLabel(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {noClassCandidates && (
                <Alert severity="info">{t("reviewQueue.attach.noRidesInClass")}</Alert>
              )}

              <FormControl fullWidth disabled={noClassCandidates}>
                <InputLabel id="attach-target-ride-label">
                  {t("reviewQueue.attach.targetRide")}
                </InputLabel>
                <Select
                  labelId="attach-target-ride-label"
                  label={t("reviewQueue.attach.targetRide")}
                  value={selectedTargetId}
                  onChange={(e) => {
                    setSelectedTargetId(e.target.value);
                    // The confirm is per target (#3354).
                    setConfirmMismatch(false);
                    setChangeTargetTest(false);
                    setServerMismatch(false);
                  }}
                >
                  {classCandidates.map((c) => {
                    const id = c.id ?? c.rideId;
                    const isSuggested = suggested && (suggested.id ?? suggested.rideId) === id;
                    // #865 — a candidate ride with no rider/horse name is a
                    // data problem to surface quietly, not the raw ride id;
                    // the id — useful for support telling two nameless
                    // candidates apart — goes in a tooltip instead. MenuItem
                    // must stay Select's direct child, so the Tooltip wraps
                    // only the label inside it.
                    const nameMissing = !c.riderName && !c.horseName;
                    // #2102 — candidates now span the whole show, so the
                    // class is shown on every row (not just a tooltip) to
                    // tell same-test rides in different classes apart.
                    const candidateClass = c.classId ? classesById[c.classId] : null;
                    const classLabel = candidateClass
                      ? classNumberLabel(candidateClass)
                      : c.classId || t("common.unknownClass");
                    const label = [
                      classLabel,
                      c.competitorNumber != null ? `#${c.competitorNumber}` : null,
                      c.riderName || c.horseName || t("common.unknownUser"),
                    ]
                      .filter(Boolean)
                      .join(" — ");
                    return (
                      <MenuItem key={id} value={id}>
                        <Tooltip title={nameMissing ? id : ""}>
                          <span>
                            {label}
                            {isSuggested ? ` (${t("reviewQueue.attach.suggested")})` : ""}
                          </span>
                        </Tooltip>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.attach.manualTest")}
                  </Typography>
                  {/* #865 — a testCode lookup miss is a data problem to surface
                      quietly (the existing "no test" copy), not the raw
                      testId; the id — useful for support diagnosing a stale
                      catalog reference — goes in a tooltip instead. */}
                  <Tooltip title={!manualTest && testSavedId ? testSavedId : ""}>
                    {/* #1878 — prefer the catalog's own clean name over FVD's raw testCode. */}
                    <Typography>
                      {manualTest?.name || manualTest?.testCode || t("reviewQueue.attach.noTest")}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.attach.targetTest")}
                  </Typography>
                  <Tooltip
                    title={
                      selectedTarget && !targetTest && selectedTarget.testId ? selectedTarget.testId : ""
                    }
                  >
                    {/* #1878 — prefer the catalog's own clean name over FVD's raw testCode. */}
                    <Typography>
                      {selectedTarget
                        ? targetTest?.name || targetTest?.testCode || t("reviewQueue.attach.noTest")
                        : "-"}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>

              {mismatch && (
                <Alert severity="warning">
                  {t("reviewQueue.attach.mismatchBlocked")}
                  {/* #3366 — MUI gives FormControlLabel a -11px left margin, which the
                      Alert message's overflow clipped on the wrapped line, and display:block
                      stacked the ripple over the text. Flex row with no negative margin, a
                      padding-free checkbox, and body2 so the label matches the alert copy. */}
                  <FormControlLabel
                    sx={{ display: "flex", alignItems: "flex-start", ml: 0, mr: 0, mt: 0.5 }}
                    slotProps={{ typography: { variant: "body2" } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mr: 1, mt: "1px" }}
                        checked={confirmMismatch}
                        onChange={(e) => {
                          setConfirmMismatch(e.target.checked);
                          if (e.target.checked) setChangeTargetTest(false);
                        }}
                      />
                    }
                    label={t("reviewQueue.attach.mismatchConfirm")}
                  />
                  {/* #3535 -- fix the real ride instead of crossing the mismatch. */}
                  <FormControlLabel
                    sx={{ display: "flex", alignItems: "flex-start", ml: 0, mr: 0, mt: 1 }}
                    slotProps={{ typography: { variant: "body2" } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mr: 1, mt: "1px" }}
                        checked={changeTargetTest}
                        onChange={(e) => {
                          setChangeTargetTest(e.target.checked);
                          if (e.target.checked) setConfirmMismatch(false);
                        }}
                      />
                    }
                    label={
                      manualTest?.name || manualTest?.testCode
                        ? t("reviewQueue.attach.changeTargetTest", { test: manualTest.name || manualTest.testCode })
                        : t("reviewQueue.attach.changeTargetTestUnnamed")
                    }
                  />
                  {changeTargetTest && (
                    <Typography variant="body2" sx={{ mt: 0.5, ml: "28px" }}>
                      {t("reviewQueue.attach.changeTargetTestNote")}
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("reviewQueue.attach.cancel")}</Button>
        <Button
          variant="contained"
          disabled={
            !selectedTargetId || !selectedTarget || (mismatch && !confirmMismatch && !changeTargetTest) || resolving || noCandidates
          }
          onClick={handleAttach}
        >
          {resolving ? <CircularProgress size={20} /> : t("reviewQueue.attach.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
